import { useEffect, useRef, useState } from "react";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { HeartPulse, RotateCcw, Hand, CheckCircle2, AlertTriangle } from "lucide-react";
import { Briefing } from "./Shared";

// Live CPR compression beat: tap "Compress" in time with the metronome guide.
// Graded on the proportion of compressions delivered in the 100–120/min band.
export function CprBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const target = beat.target || { low: 100, high: 120, ideal: 110 };
  const reps = beat.reps || 16;
  const full = beat.full ?? 20;
  const paceMs = Math.round(60000 / target.ideal);

  const [taps, setTaps] = useState([]);
  const [depth, setDepth] = useState(0); // 0 recoiled · 1 compressed
  const [lastRate, setLastRate] = useState(null);
  const [res, setRes] = useState(null);
  const done = !!res;
  const recoil = useRef(null);
  const doneRef = useRef(false);

  const grade = (stamps) => {
    const intervals = [];
    for (let i = 1; i < stamps.length; i += 1) intervals.push(stamps[i] - stamps[i - 1]);
    const rates = intervals.map((ms) => 60000 / ms);
    const inBand = rates.filter((r) => r >= target.low && r <= target.high).length;
    const accuracy = rates.length ? inBand / rates.length : 0;
    const mean = rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : 0;
    return {
      earned: Math.round(full * accuracy),
      max: full,
      detail: `Mean ${mean}/min · ${inBand}/${rates.length} compressions in the 100–120 band`,
      key: beat.key,
      label: beat.label,
      mean,
      inBand,
      total: rates.length,
    };
  };

  const compress = () => {
    if (doneRef.current) return;
    const now = performance.now();
    setDepth(1);
    clearTimeout(recoil.current);
    recoil.current = setTimeout(() => setDepth(0), 130);
    setTaps((prev) => {
      const next = [...prev, now];
      if (prev.length) setLastRate(Math.round(60000 / (now - prev[prev.length - 1])));
      if (next.length >= reps) {
        doneRef.current = true;
        setRes(grade(next));
      }
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" && !doneRef.current) {
        e.preventDefault();
        compress();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    doneRef.current = false;
    setTaps([]);
    setLastRate(null);
    setRes(null);
    setDepth(0);
  };

  const count = taps.length;
  const inBand = lastRate != null && lastRate >= target.low && lastRate <= target.high;
  const tone = lastRate == null ? "#64748b" : inBand ? "#39FF14" : lastRate < target.low ? "#FFEA00" : "#FF3333";
  const msg =
    lastRate == null ? "Tap to start" : inBand ? "On target" : lastRate < target.low ? "Push faster" : "Ease off — too fast";

  const onPrimary = () => {
    if (done) onDone({ earned: res.earned, max: res.max, detail: res.detail, key: res.key, label: res.label });
  };

  return (
    <BeatShell
      Icon={HeartPulse}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={done ? (beat.last ? "See debrief →" : "Continue →") : "Deliver the compressions to continue"}
      onPrimary={onPrimary}
      primaryDisabled={!done}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      <div className="rounded-lg border-2 border-[#222] bg-[#050505] p-4 shadow-xl" data-testid="cpr-console">
        {/* metronome guide + live rate */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9 flex items-center justify-center">
              <span
                className="absolute inset-0 rounded-full"
                style={{ background: "#dc2626", animation: `cprPace ${paceMs}ms ease-in-out infinite` }}
              />
              <HeartPulse className="w-4 h-4 text-white relative" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono-clinical text-slate-500">Pace guide</div>
              <div className="text-sm font-mono-clinical font-bold text-rose-300">
                {target.ideal}/min · aim {target.low}–{target.high}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono-clinical text-slate-500">Your rate</div>
            <div className="text-2xl font-mono-clinical font-bold leading-none" style={{ color: tone }} data-testid="cpr-rate">
              {lastRate == null ? "--" : lastRate}
              <span className="text-xs"> /min</span>
            </div>
          </div>
        </div>

        {/* compression visual */}
        <div className="mt-4 h-28 relative flex flex-col items-center justify-end">
          <div
            className="flex flex-col items-center"
            style={{ transform: `translateY(${depth ? 26 : 0}px)`, transition: "transform 90ms ease-out" }}
          >
            <Hand className="w-7 h-7 text-slate-300" />
            <div className="w-1 h-4 bg-slate-500" />
          </div>
          <div
            className="w-44 h-10 rounded-[50%] bg-gradient-to-b from-[#3a2b2b] to-[#1a1212] border border-[#4a3030]"
            style={{ transform: `scaleY(${depth ? 0.62 : 1})`, transformOrigin: "bottom", transition: "transform 90ms ease-out" }}
          />
          <div className="text-[10px] uppercase tracking-widest font-mono-clinical text-slate-600 mt-1">
            Patient thorax · allow full recoil
          </div>
        </div>

        {/* compress button + counter */}
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={compress}
            disabled={done}
            data-testid="cpr-compress-btn"
            className="flex-1 h-14 rounded-lg bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white font-bold text-base tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            COMPRESS
          </button>
          <div className="text-center min-w-[64px]">
            <div className="text-2xl font-mono-clinical font-bold" style={{ color: msg === "On target" ? "#39FF14" : "#fff" }} data-testid="cpr-count">
              {count}
              <span className="text-sm text-slate-500">/{reps}</span>
            </div>
            <div className="text-[10px] font-mono-clinical" style={{ color: tone }}>{msg}</div>
          </div>
        </div>
        <div className="text-[10px] text-slate-500 mt-2 text-center">Tap COMPRESS (or press the space bar) in time with the red pulse.</div>
      </div>

      {!done && count > 0 && (
        <button
          type="button"
          onClick={reset}
          data-testid="cpr-reset-btn"
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Restart compressions
        </button>
      )}

      {done && (
        <div className="rounded-md border-2 border-slate-200 bg-slate-50 p-4" data-testid="cpr-result">
          <div className="flex items-center gap-2">
            {res.earned >= full * 0.8 ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
            <span className="text-sm font-bold text-slate-900">
              Mean rate {res.mean}/min · {res.inBand}/{res.total} in the 100–120 band
            </span>
          </div>
          <ul className="mt-2.5 space-y-1 text-[12px] text-slate-600 leading-snug">
            <li><span className="text-emerald-600 font-semibold">›</span> Effective CPR is 100–120 compressions/min — too slow under-perfuses, too fast prevents refill.</li>
            <li><span className="text-emerald-600 font-semibold">›</span> Compress 1/3–1/2 of chest width and allow <em>full recoil</em> between compressions.</li>
            <li><span className="text-emerald-600 font-semibold">›</span> Rotate the compressor every ~2 minutes to prevent fatigue and keep quality high.</li>
          </ul>
          <button
            type="button"
            onClick={reset}
            data-testid="cpr-retry-btn"
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#0047AB] hover:underline"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Try again before continuing
          </button>
        </div>
      )}
    </BeatShell>
  );
}
